import React, { useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  CircularProgress,
} from "@mui/material";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosConfig } from "../../../axios/axiosConfig";
import toast from "react-hot-toast";
import ReactHotToaster from "../../common/ReactHotToaster";
import MuiLoadingSpinner from "../../common/MuiLoadingSpinner";
import UserRefundsTable from "../AdminRefunds/UserRefundsTable";
import RefundModuleAccess from "../RefundModuleAccess";
import LockOpenIcon from "@mui/icons-material/LockOpen";

const REFUND_STATUS_CHOICES = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "processed", label: "Processed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "under_review", label: "Under Review" },
  { value: "refund_initiated", label: "Refund Initiated" },
];

const STATUS_CONFIG = {
  pending: { label: "Pending", bg: "#fef9c3", color: "#854d0e" },
  approved: { label: "Approved", bg: "#dbeafe", color: "#1d4ed8" },
  under_review: { label: "Under Review", bg: "#ffedd5", color: "#c2410c" },
  refund_initiated: { label: "Refund Initiated", bg: "#ede9fe", color: "#6d28d9" },
  processed: { label: "Processed", bg: "#dcfce7", color: "#15803d" },
  rejected: { label: "Rejected", bg: "#fee2e2", color: "#b91c1c" },
  failed: { label: "Failed", bg: "#fee2e2", color: "#b91c1c" },
  cancelled: { label: "Cancelled", bg: "#f1f5f9", color: "#64748b" },
};

const getStatusChip = (status) => {
  const cfg = STATUS_CONFIG[status] || { label: status || "N/A", bg: "#f1f5f9", color: "#64748b" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 600, fontSize: "11px", bgcolor: cfg.bg, color: cfg.color }}
    />
  );
};

const fmt = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const headCellSx = {
  fontWeight: 700,
  fontSize: "11px",
  color: "#fff",
  background: "#04343a",
  borderBottom: "none",
  padding: "10px 14px",
  whiteSpace: "nowrap",
};


const InfoField = ({ label, value, bold }) => (
  <Box>
    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.4 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "13px", fontWeight: bold ? 700 : 500, color: bold ? "#016551" : "#1e293b" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const EditRefundDialog = ({ open, item, onClose, onSuccess }) => {
  const [status, setStatus] = useState(item?.status || "");

  React.useEffect(() => {
    if (item) setStatus(item.status || "");
  }, [item]);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await axiosConfig({
        method: "post",
        url: "payment/admin-refund-request-list/",
        data: { id: item.id, status },
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Status updated successfully");
      onSuccess(item.id, status);
      onClose();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.response || "Failed to update status");
    },
  });

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.18)" } }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #04343a 0%, #016551 100%)",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "16px", lineHeight: 1.2 }}>
            Edit Refund Request
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "12px" }}>
            ID #{item.id} · Payment {item.payment}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "rgba(255,255,255,0.7)",
            "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {/* Info card */}
        <Box
          sx={{
            background: "#f8fafc",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            p: 2.5,
            mb: 2.5,
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
            <InfoField label="Username" value={item.username} />
            <InfoField label="Email" value={item.email} />
            <InfoField label="Phone" value={item.phoneno} />
            <InfoField label="Payment ID" value={item.payment} />
            <InfoField label="Paid Amount" value={"₹" + item.payment_amount} bold />
            <InfoField
              label="Refund Amount"
              value={item.refund_amount != null ? "₹" + item.refund_amount : null}
            />
          </Box>
          {item.reason && (
            <>
              <Divider sx={{ my: 2 }} />
              <InfoField label="Reason" value={item.reason} />
            </>
          )}
        </Box>

        {/* Status edit */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            p: 2,
          }}
        >
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#64748b", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Update Status
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#016551" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#016551" },
              }}
            >
              {REFUND_STATUS_CHOICES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px", py: 1 }}>
                  {getStatusChip(opt.value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: "#64748b",
            fontWeight: 600,
            fontSize: "13px",
            borderRadius: "9px",
            px: 3,
            py: 1,
            textTransform: "none",
            border: "1px solid #e2e8f0",
            "&:hover": { background: "#f8fafc" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => mutation.mutate()}
          disabled={mutation.isLoading || status === item.status}
          startIcon={mutation.isLoading ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            background: "linear-gradient(135deg, #016551, #014d3d)",
            color: "#fff",
            fontWeight: 700,
            fontSize: "13px",
            borderRadius: "9px",
            px: 3,
            py: 1,
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)", boxShadow: "0 6px 16px rgba(1,101,81,0.4)" },
            "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RefundRequestsTab = () => {
  const [data, setData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editItem, setEditItem] = useState(null);

  const query = useQuery({
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    queryKey: ["admin-refund-request-list"],
    queryFn: async () => {
      const res = await axiosConfig({ method: "get", url: "payment/admin-refund-request-list/" });
      return res.data;
    },
    onSuccess: (d) => setData(d.response || []),
    onError: (err) => toast.error(err?.response?.data?.response || "Failed to load refund requests"),
  });

  const handleStatusUpdate = (id, newStatus) => {
    setData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item))
    );
  };

  const filtered = searchTerm
    ? data.filter(
        (item) =>
          String(item.payment).includes(searchTerm) ||
          (item.reason || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : data;

  return (
    <>
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: "#04343a",
            px: "20px",
            py: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>
              Refund Requests
            </Typography>
            <Chip
              label={"Total: " + filtered.length}
              size="small"
              sx={{ bgcolor: "rgba(1,101,81,0.3)", color: "#fff", fontWeight: 600, fontSize: "12px" }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search by payment ID or reason"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setSearchTerm(searchInput.trim())}
              sx={{
                width: 240,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "13px",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                  "&.Mui-focused fieldset": { borderColor: "#fff" },
                },
                "& .MuiOutlinedInput-input": { color: "#fff" },
                "& .MuiOutlinedInput-input::placeholder": { color: "rgba(255,255,255,0.5)" },
              }}
            />
            <Button
              size="small"
              onClick={() => setSearchTerm(searchInput.trim())}
              disabled={!searchInput.trim()}
              startIcon={<SearchIcon />}
              sx={{
                background: "linear-gradient(135deg, #016551, #014d3d)",
                color: "#fff",
                fontWeight: 600,
                borderRadius: "7px",
                px: 2,
                fontSize: "12px",
                "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
                "&.Mui-disabled": { background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)" },
              }}
            >
              Search
            </Button>
          </Box>
        </Box>

        {query.isLoading ? (
          <MuiLoadingSpinner />
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderTop: "1px solid #eef2f7", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 45 }}>S.No.</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 130 }}>Username</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 170 }}>Email</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 120 }}>Phone</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 140 }}>Reason</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 100 }}>Payment ID</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 110 }}>Paid Amount</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 120 }}>Refund Amount</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 140 }}>Status</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 150 }}>Created At</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 150 }}>Modified At</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 60 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 5, color: "#94a3b8", fontSize: "13px" }}>
                      No refund requests found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((item, index) => (
                    <TableRow
                      key={item.id}
                      sx={{ "&:hover": { background: "#f0faf5" }, "& > td": { borderBottom: "1px solid #f0f4f8" } }}
                    >
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: "#94a3b8" }}>{index + 1}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: "#1e293b" }}>{item.username || "—"}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: "#64748b" }}>{item.email || "—"}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: "#64748b" }}>{item.phoneno || "—"}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: "#64748b" }}>{item.reason || "—"}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: "#64748b" }}>{item.payment}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: "#1e293b", fontWeight: 600 }}>
                        {"₹" + item.payment_amount}
                      </TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: "#64748b" }}>
                        {item.refund_amount != null ? "₹" + item.refund_amount : "—"}
                      </TableCell>
                      <TableCell sx={{ padding: "8px 14px" }}>{getStatusChip(item.status)}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: "#64748b" }}>{fmt(item.created_at)}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: "#64748b" }}>{fmt(item.modified_at)}</TableCell>
                      <TableCell sx={{ padding: "4px 8px" }}>
                        <IconButton
                          size="small"
                          onClick={() => setEditItem(item)}
                          sx={{
                            color: "#016551",
                            "&:hover": { background: "#e6f4f0", color: "#014d3d" },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <EditRefundDialog
        open={Boolean(editItem)}
        item={editItem}
        onClose={() => setEditItem(null)}
        onSuccess={handleStatusUpdate}
      />
    </>
  );
};

const ProcessedRefundsTab = () => {
  const [refundsData, setRefundsData] = useState([]);

  const query = useQuery({
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    queryKey: ["user-refunds"],
    queryFn: async () => {
      const res = await axiosConfig({ method: "get", url: "payment/moneyback" });
      return res.data;
    },
    onSuccess: (d) => setRefundsData(d.response || []),
    onError: (err) => toast.error(err?.response?.data?.response || "Failed to load refunds"),
  });

  return query.isLoading ? (
    <MuiLoadingSpinner />
  ) : (
    <UserRefundsTable userRefundsData={refundsData} setUserRefundsData={setRefundsData} />
  );
};

const RefundDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <MoneyOffIcon sx={{ color: "#fff", fontSize: 32 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
            Refund Dashboard
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
            Manage refund requests and processed refunds
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          mb: 3,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 2,
            "& .MuiTab-root": {
              fontWeight: 600,
              fontSize: "13px",
              textTransform: "none",
              color: "#64748b",
              minHeight: 48,
              gap: 0.75,
            },
            "& .Mui-selected": { color: "#016551 !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#016551", height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          <Tab label="Refund Requested" icon={<PendingActionsIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
          <Tab label="Processed Refunds" icon={<CheckCircleOutlineIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
          <Tab label="Module Access" icon={<LockOpenIcon sx={{ fontSize: 17 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {activeTab === 0 && <RefundRequestsTab />}
      {activeTab === 1 && <ProcessedRefundsTab />}
      {activeTab === 2 && <RefundModuleAccess asTab />}

      <ReactHotToaster />
    </Box>
  );
};

export default RefundDashboard;
